import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Stack,
  Chip,
  Button,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const PDFViewer = ({ documentUrl, travelerUuid, documentId, title }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isPDF, setIsPDF] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost') ? 'http://localhost:3001' : '');

  useEffect(() => {
    if (documentId && travelerUuid) {
      fetchPDFInfo();
    }
  }, [documentId, travelerUuid]);

  const fetchPDFInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${baseUrl}/api/documents/public/${documentId}/pages/${travelerUuid}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to get PDF information');
      }
      
      setIsPDF(data.isPDF);
      setTotalPages(data.pageCount || 1);
      
      console.log('PDF Info:', data);
    } catch (err) {
      console.error('Failed to fetch PDF info:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setImageError(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  const handleImageError = () => {
    console.error(`Failed to load page ${currentPage}`);
    setImageError(true);
  };

  const getImageUrl = (page) => {
    if (!isPDF) {
      // For non-PDF documents, use the original document viewer
      return documentUrl;
    }
    return `${baseUrl}/api/documents/public/${documentId}/image/${travelerUuid}?page=${page}&t=${Date.now()}`;
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading document...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="subtitle2">Unable to load document</Typography>
        <Typography variant="body2">{error}</Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls - Only show for images, PDFs have their own built-in controls */}
      {!isPDF && (
        <Box sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          backgroundColor: 'grey.50'
        }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label="Image Document"
              size="small"
              color="secondary"
            />
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.5}>
              <ZoomOutIcon />
            </IconButton>

            <Typography variant="caption" sx={{ minWidth: '50px', textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>

            <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 3}>
              <ZoomInIcon />
            </IconButton>

            <IconButton size="small" onClick={fetchPDFInfo} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* Document Viewer */}
      <Box sx={{
        flex: 1,
        overflow: 'hidden',
        backgroundColor: '#f5f5f5',
      }}>
        {isPDF ? (
          // For PDFs, use secure PDF.js viewer
          <iframe
            src={`/pdfjs-viewer.html?url=${encodeURIComponent(documentUrl)}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title={title || 'PDF Document'}
            sandbox="allow-scripts"
          />
        ) : (
          // For images, use the original image display
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 2,
            overflow: 'auto'
          }}>
            {imageError ? (
              <Alert severity="warning" sx={{ maxWidth: 400 }}>
                <Typography variant="subtitle2">Document failed to load</Typography>
                <Typography variant="body2">Document could not be displayed</Typography>
                <Box sx={{ mt: 2 }}>
                  <Button variant="outlined" size="small" onClick={fetchPDFInfo}>
                    Retry Loading
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Box
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease-in-out',
                }}
              >
                <img
                  src={documentUrl}
                  alt={title || 'Document'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    backgroundColor: 'white',
                  }}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  onDragStart={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Document Info */}
      <Box sx={{ 
        p: 1, 
        borderTop: 1, 
        borderColor: 'divider',
        backgroundColor: 'grey.50',
        textAlign: 'center'
      }}>
        <Typography variant="caption" color="text.secondary">
          {title} • View-only • 
          {isPDF ? ` ${totalPages} page${totalPages !== 1 ? 's' : ''} •` : ''} 
          Secure Document Viewer
        </Typography>
      </Box>
    </Box>
  );
};

export default PDFViewer;